import React, {createContext, useEffect, useState} from "react";
import axios from "axios";
import {loginAPI} from "../services/AuthService";
import {setSnackbarOpen} from "../store/slices/snackbar.slice";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";

type UserContextType = {
    token: string | null;
    loginUser: (email: string, password: string) => void;
    logout: () => void;
    isLoggedIn: () => boolean;
}

type Props = { children: React.ReactNode };

const UserContext = createContext<UserContextType>({} as UserContextType);

export const UserProvider = ({ children }: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [token, setToken] = useState<string | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setToken(token);
        }
        setIsReady(true);
    }, []);

    const handleError = (error: any) => {
        if (axios.isAxiosError(error)) {
            const err = error.response;
            if (Array.isArray(err?.data.errors)) {
                let errorMessage = "";
                for (let val of err?.data.errors) {
                    errorMessage += val.description;
                }
                dispatch(setSnackbarOpen({snackbarMessage: errorMessage, snackbarSeverity: 'error'}));
            } else if (typeof err?.data.errors === "object") {
                let errorMessage = "";
                for (let e in err?.data.errors) {
                    errorMessage += err.data.errors[e][0];
                }
                dispatch(setSnackbarOpen({snackbarMessage: errorMessage, snackbarSeverity: 'error'}));
            } else if (err?.data) {
                dispatch(setSnackbarOpen({snackbarMessage: err.data.message, snackbarSeverity: 'error'}));
            } else if (err?.status === 401) {
                dispatch(setSnackbarOpen({snackbarMessage: "Falsche E-Mail Adresse oder falsches Passwort.", snackbarSeverity: 'error'}));
                window.history.pushState({}, "LoginPage", "/login");
            } else if (err) {
                dispatch(setSnackbarOpen({snackbarMessage: err, snackbarSeverity: 'error'}));
            }
        }
    };

    const loginUser = async (email: string, password: string) => {
        await loginAPI(email, password)
            .then((result) => {
                if (result) {
                    localStorage.setItem('token', result?.data.token ?? "");
                    setToken(result?.data.token ?? "");
                    dispatch(setSnackbarOpen({snackbarMessage: "Anmeldung erfolgreich!", snackbarSeverity: 'success'}));
                    navigate('/agents');
                }
            })
            .catch((error) => handleError(error));
    };

    const isLoggedIn = () => {
        return !!token;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken("");
    };

    return (
        <UserContext.Provider value={{loginUser, token, logout, isLoggedIn}}>
            {isReady ? children : null}
        </UserContext.Provider>
    )
};

export const useAuth = () => React.useContext(UserContext);